import { Injectable, OnDestroy } from '@angular/core';
import { Action, Store } from '@ngrx/store';
import { FilterMap } from '../../../models/locations-models/filter-map';
import { cloneDeep } from 'lodash';
import { filter, map, switchMap, take, takeUntil } from 'rxjs/operators';
import { BatchModifyLocationGrpsPopupComponent } from './popups/batch-modify-location-grps-popup/batch-modify-location-grps-popup.component';
import { Location } from '../../../models/locations-models/location';
import { Actions, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { MatDialog } from '@angular/material/dialog';
import { Clipboard } from '@angular/cdk/clipboard';
import { FeedbackService } from '../../../core/feedback.service';
import { HttpErrorResponse } from '@angular/common/http';
import { OngoingActionsEnum } from '../enums/ongoing-actions';
import { ApiService } from '../../../core/api-service';
import { ApiResponseWithPotentialErrors } from '../../../models/locations-models/api-response-with-potential-errors';
import { convertJsonToMap, sendToGoogleAnalytics } from '../../../core/utilities';
import { getFieldGroupOfFieldId, getFullNameForFieldID } from '../location-utils';
import { UpdateGbpPopupComponent } from './popups/update-gbp-popup/update-gbp-popup.component';
import { Observable, of, Subject, Subscription, tap } from 'rxjs';
import { AuthenticationService } from '../../../core/authentication.service';
import { ExportLocationsPopupComponent } from './popups/export-locations-popup/export-locations-popup.component';
import { ExportPlaceNamesPopupComponent } from './popups/export-place-names-popup/export-place-names-popup.component';
import { ConfirmationDialogType } from '../../global/confirmation-popup/confirmation-popup.component';
import { ApiResponseStatus } from '../../../models/api-response';
import { GbpLocationGroup } from '../../../models/locations-models/gbp-location-group';
import * as locationListSelectors from './state/location-list-selectors';
import { LocationDetail } from '../enums/location-detail';
import { selectAccount, selectAccountID } from '../../../store/global/core-selectors';
import { LocationLevel } from '../enums/location-level';
import { BulkAcceptDifferencesPopupComponent } from './popups/bulk-accept-differences-popup/bulk-accept-differences-popup.component';
import { SelectedLocationsRadioButton } from './popups/bulk-action-popup/bulk-action-popup.component';
import { BlacklistingActions, CountryListActions, EditGbpConnectionsActions, GbpLocationGroupsActions, LocationsRefreshActions, OngoingActions, RefreshGbpConnectionsActions, toggleLocationDetail } from './state/location-list-actions';
import { LocationListApiService } from './location-list-api.service';
import { LSCField } from '../../../models/LSCFields';
import { LocationListLocalStoreService } from './location-list-local-store.service';

@Injectable()
export class LocationListService implements OnDestroy {

  private readonly componentDestroyed = new Subject<void>();

  private readonly accountID$ = this.store.select(selectAccountID);
  private readonly account$ = this.store.select(selectAccount);
  private readonly selectedLocationsFoundInMyBusiness$ = this.store
      .select(locationListSelectors.selectSelectedLocationsFoundInMyBusiness);
  private readonly selectedLocations$ = this.store.select(locationListSelectors.selectSelectedLocations);
  private readonly selectedLocationsString$ = this.store.select(locationListSelectors.selectSelectedLocationsAsString);
  private readonly displayedColumns$ = this.locationListLocalStore.displayedColumns$;

  constructor(private store: Store,
              private actions$: Actions,
              private dialog: MatDialog,
              private clipboard: Clipboard,
              private feedbackService: FeedbackService,
              private apiService: ApiService,
              // TODO: right now, authService is here to get user name for exporting functionality,
              //  should be refactored
              private authService: AuthenticationService,
              private locationListApi: LocationListApiService,
              private locationListLocalStore: LocationListLocalStoreService) {
  }

  ngOnDestroy() {
    this.componentDestroyed.next();
  }

  // TODO: ONCE store file structure is definitive, we could rework this as NGRX EFFECTS = smaller
  //  blocks of code (removes the need for switchMap()) that react to dispatched actions from this
  //  service
  // https://timdeschryver.dev/blog/start-using-ngrx-effects-for-this

  /**
   * Clear all filters
   */
  clearFilters() {
    this.locationListApi.clearFilters();
  }

  /**
   * Clear sorting
   */
  clearSorting() {
    this.locationListApi.clearSorting();
  }

  /**
   * Updates GMB groups and opens a popup to modify selected locations' GMB Group membership
   * upon a successful update.
   * @param location
   * @default locations selected in the table
   */
  openBatchModifyLocationGroupsDialog(location?: Location) {
    this.store.dispatch(OngoingActions.addOngoingAction({
      ongoingAction: OngoingActionsEnum.UPDATE_GBP_GROUPS,
    }));
    this.store.dispatch(GbpLocationGroupsActions.updateGbpLocationGroups());

    this.actions$
        .pipe(
            ofType(GbpLocationGroupsActions.updateGbpLocationGroupsSuccess,
                GbpLocationGroupsActions.updateGbpLocationGroupsError),
            concatLatestFrom((action) => [this.selectedLocationsFoundInMyBusiness$, this.accountID$]),
            take(1), // limit to 1 emission, must be placed before filter since it limits emissions
            filter(([action, locations, accountID]) => action.type ===
                GbpLocationGroupsActions.updateGbpLocationGroupsSuccess.type),
            switchMap(([action, locations, accountID]) =>
                this.dialog
                    .open(BatchModifyLocationGrpsPopupComponent, {
                      data: {
                        locations: location ? [location] : locations,
                        gbpGroups: (action as ({ gbpLocationGroups: GbpLocationGroup[] } &
                            Action<'[LOCATION LIST - GBP L. GRPs REFRESH] updateGbpLocationGroupsSuccess'>))
                            .gbpLocationGroups,
                        accountId: accountID,
                      },
                      minWidth: '448px',
                      height: '60vh',
                    }).afterClosed(),
            ))
        .subscribe()
        .add(() => this.store
        .dispatch(OngoingActions.removeOngoingAction({ ongoingAction: OngoingActionsEnum.UPDATE_GBP_GROUPS })));
  }

  /**
   * Copy selected locations' data to clipboard and show snackbar
   */
  copyStringToClipboard(payload?: string) {
    const copy = (_payload) => {
      this.clipboard.copy(_payload) ?
          this.feedbackService.showSuccessSnackbar({
            data: { userFriendlyMessage: 'Location data copied to clipboard!' },
            isAddressListSnackbar: true,
          }) :
          this.feedbackService.showWarningSnackbar({
            data: { userFriendlyMessage: 'Location data could not be copied to clipboard!' },
            isAddressListSnackbar: true,
          });
    };

    payload ? copy(payload) :
        this.selectedLocationsString$.pipe(take(1)).subscribe((_payload) => {
          copy(_payload);
        });
  }


  /**
   * Opens a popup where the fields that should be bulk updated in BLS can be selected. Updates the selected locations if
   * clicked on "Update".
   */
  openBulkAcceptDifferencesDialog() {
    this.selectedLocations$
        .pipe(
            concatLatestFrom(() => this.accountID$),
            take(1), // limit to 1 emission, has to be placed here since below filter limits emission
            switchMap(([selectedLocations, accountID]) => {
              return this.dialog
                  .open(BulkAcceptDifferencesPopupComponent, {
                    data: {
                      locations: selectedLocations,
                    },
                    panelClass: 'mat-dialog-width-transition',
                  })
                  .afterClosed()
                  .pipe(
                      map(dialogRes => ({ dialogRes, selectedLocations, accountID })),
                      filter((res) => !!res.dialogRes?.fieldsToAcceptDifference?.length),
                  );
            }),
            // side effect in data stream ? could maybe be moved to switchMap below
            // https://stackoverflow.com/questions/54289549/when-should-we-use-the-rxjs-tap-operator
            // https://www.yld.io/blog/the-not-so-scary-guide-to-functional-programming/
            tap(() => this.store.dispatch(OngoingActions.addOngoingAction({
              ongoingAction: OngoingActionsEnum.BULK_UPDATE_DIFFERENCES,
            }))),
            switchMap((res) => {
              const fieldMap: Map<string, boolean> = new Map();
              const locationIds: number[] = res.selectedLocations.map(l => l.id);

              res.dialogRes.fieldsToAcceptDifference.forEach((diffToAccept) => {
                fieldMap.set(diffToAccept, true);
              });

              return this.apiService
                  .bulkEditDifferenceAcceptance(res.accountID, locationIds, fieldMap);
            }),
            takeUntil(this.componentDestroyed))
        .subscribe({
          next: (res) => {
            if (res.result?.failedLocationStoreCodes?.length) {
              // partial success
              this.feedbackService.showWarningSnackbar({
                data: {
                  userFriendlyMessage: `Differences of some locations could not be updated (see details for failed locations' store codes)`,
                  detailedInfoFromBE: res.result.failedLocationStoreCodes
                }
              });
            } else {
              this.feedbackService.showSuccessSnackbar({
                data: { userFriendlyMessage: 'Differences for selected locations have been updated!' },
                isAddressListSnackbar: true,
                duration: 3000,
              });
            }

            if (res.result?.successfulLocations.length) {
              this.store.dispatch(LocationsRefreshActions.patchLocations({locations: res.result.successfulLocations}));
            }
          },
          error: ((err: HttpErrorResponse) => {
            this.feedbackService.showAlertSnackbar({
              data: {
                userFriendlyMessage: 'Accepting/Unaccepting differences failed.',
                detailedInfoFromBE: err.error?.failedLocationStoreCodes,
              }, isAddressListSnackbar: true,
            }, err?.status);
          }),
        }).add(() => this.store
        .dispatch(OngoingActions.removeOngoingAction({ ongoingAction: OngoingActionsEnum.BULK_UPDATE_DIFFERENCES })));
  }

  /**
   * Opens up update in GBP popup and sends event to Google Analytics
   * @param locations // used when right-clicking a location from the table
   */
  updateInGbpAndSendToGoogleAnalytics(locations?: Location[]) {
    this.selectedLocations$.pipe(
        concatLatestFrom(() => [this.accountID$, this.displayedColumns$]),
        take(1), // limit to 1 emission, has to be placed here since below filter limits emission
        switchMap(([selectedLocations, accountID, displayedColumns]) => {
          const _locations = locations ? locations : selectedLocations;
          return this.dialog.open(UpdateGbpPopupComponent, {
            data: {
              selectedLocationsCount: _locations.length,
              displayedColumns: displayedColumns,
            },
            width: 'fit-content'
          }).afterClosed().pipe(
              filter((dialogRes) => !!dialogRes?.selectedFields?.length),
              map((dialogRes) => {
                const locationIds = _locations.map(l => l.id);
                const blacklistedFieldsSelected = _locations.some(loc => loc.blackListedFields?.some(blf => dialogRes?.selectedFields.includes(blf)));
                return { dialogRes, accountID, locationIds, blacklistedFieldsSelected };
              }),
          );
        }),
        tap((res) => { // side effect
          this.store.dispatch(OngoingActions.addOngoingAction({ ongoingAction: OngoingActionsEnum.UPDATE_IN_GBP }));
          sendToGoogleAnalytics(
              res?.locationIds?.length > 1 ? 'Multi-location action' : 'Single-location action',
              'update in GMB',
              res?.locationIds?.length > 1 ? 'Update multiple locations in GMB' : 'Update single location in GMB');
        }),
        switchMap((res) => this.apiService
            .updateLocationsInGbp(res.accountID, res.locationIds, res.dialogRes.selectedFields)
            .pipe(
                map(apiCallResponse => ({
                      apiCallResponse,
                      locationIds: res.locationIds,
                      blacklistedFieldsSelected: res.blacklistedFieldsSelected,
                    }),
                ),
            )),
        takeUntil(this.componentDestroyed))
        .subscribe({
          next: (res) => {
            if (res.apiCallResponse?.result?.failedLocations?.length) {
              this.feedbackService.showWarningSnackbar({
                data: {
                  userFriendlyMessage: 'Updating some locations in GBP failed (click \'show details\' for more information).',
                  detailedInfoFromBE: res.apiCallResponse?.result?.failedLocations?.map(location => `${location.storeCode}: ${location.lastUpdateErrorMessage}`),
                  actionButtons: [{
                    text: 'Filter for locations with error',
                    action: this.filterForGbpErrors.bind(this),
                  }],
                },
              });
            } else {
              this.feedbackService.showSuccessSnackbar({
                data: {
                  userFriendlyMessage: 'GBP update was successful for all selected locations!',
                },
              });
            }

            if (res.apiCallResponse.result?.successfulLocations?.length) {
              this.store.dispatch(LocationsRefreshActions.patchLocations({locations: res.apiCallResponse.result.successfulLocations}));
            }
          },
          error: (err: HttpErrorResponse) => {
            this.feedbackService.showAlertSnackbar({
              data: {
                userFriendlyMessage: 'Updating in GBP failed.',
                detailedInfoFromBE: err?.error?.message,
              },
            }, err.status);
          },
        }).add(() => {
          this.store.dispatch(OngoingActions.removeOngoingAction({ ongoingAction: OngoingActionsEnum.UPDATE_IN_GBP }));
    });
  }

  /**
   * Moves selected locations to the archive.
   * @param locations
   * @default locations selected in the table
   */
  archiveLocations(locations?: Location[]) {
    this.selectedLocations$
        .pipe(
            concatLatestFrom(() => this.accountID$),
            take(1), // limit to 1 emission, has to be placed here since below filter limits emission
            map(([selectedLocations, accountID]) => {
              const _locations = locations ? locations : selectedLocations;
              const locationIds = _locations.map(l => l.id);
              return { locationIds, accountID };
            }),
            filter((res) => !!res.locationIds.length),
            switchMap((res) =>
                this.feedbackService.showConfirmationPopup({
                  data: {
                    title: 'Archive selected locations',
                    text: 'Do you really want to archive the selected locations?',
                    submitButtonText: 'Archive',
                    cancelButtonText: 'Cancel',
                  },
                }).pipe(
                    map((shouldArchive: boolean) => shouldArchive ?
                        { locationIds: res.locationIds, accountID: res.accountID } : null),
                    filter((res) => !!res),
                ),
            ),
            tap(() => this.store  // side effect
                .dispatch(OngoingActions.addOngoingAction({ ongoingAction: OngoingActionsEnum.ARCHIVING }))),
            switchMap((res) =>
                this.apiService
                    .archiveLocations(res.accountID, res.locationIds)),
            takeUntil(this.componentDestroyed),
        )
        .subscribe({
          next: (res) => {
            const locationIds: string[] = res.result?.map(l => l?.importedLocationId?.toString());

            this.store.dispatch(LocationsRefreshActions.removedLocations({ids: locationIds}));
            this.feedbackService.showSuccessSnackbar({
              data: {
                userFriendlyMessage:
                    locationIds.length +
                    ' ' +
                    (locationIds.length > 1 ? 'locations' : 'location') +
                    ' archived.',
              }, isAddressListSnackbar: true,
            });
          },
          error: ((err: HttpErrorResponse) => {
            this.feedbackService.showAlertSnackbar({
              data: {
                userFriendlyMessage: 'Archiving locations failed.',
                detailedInfoFromBE: err?.error?.message ? [err.error.message] : null,
              }, isAddressListSnackbar: true,
            }, err?.status);
          }),
        }).add(() => this.store
        .dispatch(OngoingActions.removeOngoingAction({ ongoingAction: OngoingActionsEnum.ARCHIVING })));
  }

  /**
   * Refreshes BLS locations (get updates from GBP).
   * @param locations List of locations you want to refresh. If null, will take the selected locations.
   * @default locations selected in the table
   */
  refreshGbpConnections(locations?: Location[]) {
    if (locations) {
      this.store.dispatch(RefreshGbpConnectionsActions.refreshGbpConnections({ locations }));
    } else {
      this.selectedLocations$.pipe(take(1)).subscribe(selectedLocations =>
          this.store.dispatch(RefreshGbpConnectionsActions.refreshGbpConnections({ locations: selectedLocations })),
      );
    }
  }

  /**
   * Edit the connection between a BLS location and a GBP location.
   * @param location Location for which the connection to GBP will be changed.
   * @param command Defines which change should be performed: 'create' or 'remove' a connection to a GBP location.
   */
  editGBPConnection(location: Location, command: string) {
    this.store.dispatch(EditGbpConnectionsActions.editGbpConnections({ location, command }));
  }

  /**
   * Opens a popup where the fields that should be blacklisted/whitelisted can be selected. Updates
   * the blacklisted status of the selected locations if clicked on "Update Blacklisted Fields".
   * @param locations If not null, will update blacklisted fields for these locations, otherwise will take the selected locations.
   * @default locations selected in the table
   */
  openBlacklistFieldsDialog(locations?: Location[]) {
    this.selectedLocations$.pipe(
        concatLatestFrom(() => this.displayedColumns$),
        take(1)).subscribe(([selectedLocations, displayedColumns]) => {
      if (locations) {
        this.store.dispatch(BlacklistingActions.blacklistFields({ locations, displayedColumns }));
      } else {
        this.store.dispatch(BlacklistingActions.blacklistFields({ locations: selectedLocations, displayedColumns }));
      }
    });
  }

  /**
   * Uploads the csv file if one has just been selected. Importing is being triggered through the
   * action bar - import button
   */
  onImportFileSelected(importedFile): Subscription {
    this.store.dispatch(OngoingActions.addOngoingAction({ ongoingAction: OngoingActionsEnum.IMPORTING }));
    sendToGoogleAnalytics('Import location data', 'Import locations', 'Import locations from CSV');

    const subscription = this.accountID$
        .pipe(
            take(1),
            switchMap((accountID) => this.apiService
                .importLocations(accountID, importedFile)),
        )
        .subscribe({
          next: (res) => {
            this.feedbackService.showSuccessSnackbar({
              data: {
                userFriendlyMessage: 'Locations have been imported successfully!',
              }, isAddressListSnackbar: true,
            });
            // the import might've appended a country or two so let's force-refresh the column filter opts
            this.store.dispatch(CountryListActions.updateCountryList());
            // update locations in store
            if (res.result?.length) {
              this.store.dispatch(LocationsRefreshActions.patchLocations({locations: res.result}));
            }
          }, error: (err: HttpErrorResponse) => {
            this.feedbackService.showAlertSnackbar({
              data: {
                userFriendlyMessage: 'Importing addresses failed.',
                detailedInfoFromBE: (err?.error?.message === 'NO_CSV') ?
                    ['The file you tried to upload is not a CSV! Please choose a CSV-file!'] :
                    err?.error?.message ? [err.error.message] : null,
                actionButtons: (err.error.message === 'NO_CSV') ? null : [{
                  text: 'Filter for locations with error',
                  action: this.filterForGbpErrors.bind(this),
                }],
              },
              isAddressListSnackbar: true,
            }, err?.status);
          },
        });

    // .add() no longer returns a Subscription as of rxjs v7
    subscription.add(() => {
      this.store.dispatch(OngoingActions.removeOngoingAction({ ongoingAction: OngoingActionsEnum.IMPORTING }));
    });

    return subscription;
  }

  /**
   * Opens a popup to select which location data should be exported and opens a snackbar with the
   * option to open the Google Sheet containing the location-export in a new tab.
   */
  exportLocationsToGoogleSheets() {
    this.selectedLocations$.pipe(
        concatLatestFrom(() => this.displayedColumns$),
        take(1), // limit to 1 emission, has to be placed here since below filter limits emission
        switchMap(([selectedLocations, displayedColumns]) => {
              const filteredLocations = this.locationListApi.getFilteredLocations();
              return this.dialog
                  .open(ExportLocationsPopupComponent, {
                    height: '615px',
                    data: {
                      areLocationsSelected: !!selectedLocations.length,
                      areLocationsFiltered: !!filteredLocations?.length,
                      displayedColumns: displayedColumns,
                    },
                  })
                  .afterClosed()
                  .pipe(
                      map((exportSelection: { subsetType, selectedCsvFields }) => Boolean(exportSelection) ?
                          {
                            exportSelection,
                            filteredLocations,
                            selectedLocations,
                          } : null),
                      filter((res) => !!res))
            },
        ),
        tap((res) => {  // side effect
          this.store.dispatch(OngoingActions.addOngoingAction({ ongoingAction: OngoingActionsEnum.EXPORTING }));
          sendToGoogleAnalytics('Export location data',
              `Export ${res.exportSelection.subsetType} locations`,
              `Export ${res.exportSelection.subsetType} locations as Sheet`);
        }),
        concatLatestFrom(() => this.accountID$),
        switchMap(([res, accountId]) => {
          const fields = res.exportSelection.selectedCsvFields;
          const locationIds = this.getLocationsIdsFromLocationSubset(
              res.exportSelection.subsetType, res.filteredLocations, res.selectedLocations);
          return this.apiService.exportLocations(accountId, fields, locationIds);
        })
    ).subscribe({
      next: res => {
        this.feedbackService.showSuccessSnackbar({
          data: {
            userFriendlyMessage: 'Exporting to Google Sheets successful!',
            actionButtons: [{ text: 'Open', action: () => window.open(res.result, '_blank') }],
          },
          isAddressListSnackbar: true,
          // don't close automatically
          duration: null,
        });
        window.open(res.result, '_blank');
      },
      error: (err: HttpErrorResponse) => {
        this.feedbackService.showAlertSnackbar({
          data: {
            userFriendlyMessage: 'Exporting locations to Google Sheets failed.',
            detailedInfoFromBE: err?.error?.message ? [err.error.message] : null,
          }, isAddressListSnackbar: true,
        }, err?.status);
      },
    })
    .add(() => {
      this.store.dispatch(OngoingActions.removeOngoingAction({ ongoingAction: OngoingActionsEnum.EXPORTING }));
    });
  }

  /**
   * Exports differences of filtered locations to Google Sheets.
   */
  exportDifferencesToGoogleSheets() {
    this.store.dispatch(OngoingActions.addOngoingAction({ ongoingAction: OngoingActionsEnum.EXPORTING }));
    sendToGoogleAnalytics('Export location data',
        'Export location differences',
        'Export location differences as Sheet');

    return this.accountID$
        .pipe(
            take(1),
            switchMap((accountId) =>
                this.apiService.exportDifferences(accountId, this.locationListApi.getFilteredLocations())),
        )
        .subscribe({
          next: res => {
            this.feedbackService.showSuccessSnackbar({
              data: {
                userFriendlyMessage: 'Exporting to Google Sheets successful!',
                actionButtons: [{ text: 'Open', action: () => window.open(res.result, '_blank') }],
              },
              isAddressListSnackbar: true,
              // don't close automatically
              duration: null,
            });
            window.open(res.result, '_blank');
          },
          error: (err: HttpErrorResponse) => {
            this.feedbackService.showAlertSnackbar({
              data: {
                userFriendlyMessage: 'Exporting location differences failed.',
                detailedInfoFromBE: err?.error?.message ? [err.error.message] : null,
              }, isAddressListSnackbar: true,
            }, err?.status);
          },
        }).add(() => {
          this.store.dispatch(OngoingActions.removeOngoingAction({ ongoingAction: OngoingActionsEnum.EXPORTING }));
        });
  }

  /**
   * Exports duplicate locations to Google Sheets.
   */
  exportDuplicateLocations() {
    this.store.dispatch(OngoingActions.addOngoingAction({ ongoingAction: OngoingActionsEnum.EXPORTING }));
    sendToGoogleAnalytics('Export location data', 'Export duplicates', 'Export duplicates as Sheet');

    this.accountID$
        .pipe(
            take(1),
            switchMap((accountID) => this.apiService
                .exportDuplicateLocations(accountID)),
        ).subscribe(
        {
          next: res => {
            this.feedbackService.showSuccessSnackbar({
              data: {
                userFriendlyMessage: 'Exporting to Google Sheets successful!',
                actionButtons: [{ text: 'Open', action: () => window.open(res.result, '_blank') }],
              },
              isAddressListSnackbar: true,
              // don't close automatically
              duration: null,
            });
            window.open(res.result, '_blank');
          },
          error: (err: HttpErrorResponse) => {
            this.feedbackService.showAlertSnackbar({
              data: {
                userFriendlyMessage: 'Exporting duplicates failed.',
                detailedInfoFromBE: err?.error?.message ? [err.error.message] : null,
              }, isAddressListSnackbar: true,
            }, err?.status);
          },
        }).add(() => {
      this.store.dispatch(OngoingActions.removeOngoingAction({ ongoingAction: OngoingActionsEnum.EXPORTING }));
    });
  }

  /**
   * Exports starred C&C search results to Google Sheets.
   * @param excludeLinkedResults
   * @param importedLocationIds$ - these can be selected / filtered location IDs
   */
  exportStarredCCSearchResults(excludeLinkedResults: boolean, importedLocationIds$: Observable<number[] | null> = of(null)) {
    this.store.dispatch(OngoingActions.addOngoingAction({ ongoingAction: OngoingActionsEnum.EXPORTING }));
    sendToGoogleAnalytics('Export location data',
        excludeLinkedResults ? 'Export unlinked starred search results only' : 'Export starred search results',
        excludeLinkedResults ? 'Export unlinked starred search results only as Sheet' : 'Export starred search results as Sheet');

    this.accountID$.pipe(
        concatLatestFrom(() => importedLocationIds$),
        take(1),
        switchMap(([accountID, importedLocationIds]) => this.apiService
            .exportStarredCCSearchResults(accountID, excludeLinkedResults, importedLocationIds)),
    )
        .subscribe({
          next: res => {
            //show warning if keywords have been excluded from export; otherwise show success msg
            const hasNegativeKeywords: boolean = res.result['hasNegativeKeywords'];
            const negativeKeywordsWarning = hasNegativeKeywords ? 'Attention! There are keywords excluded in this export.\n' : '';

            const snackbarConfig = {
              data: {
                userFriendlyMessage: negativeKeywordsWarning + 'Exporting to Google Sheets successful!',
                actionButtons: [{
                  text: 'Open',
                  action: () => window.open(res.result['sheetUrl'], '_blank'),
                }],
              },
              isAddressListSnackbar: true,
              // don't close automatically
              duration: null,
            };
            hasNegativeKeywords ? this.feedbackService.showWarningSnackbar(snackbarConfig) : this.feedbackService.showSuccessSnackbar(snackbarConfig);
            window.open(res.result['sheetUrl'], '_blank');
          },
          error: (err: HttpErrorResponse) => {
            this.feedbackService.showAlertSnackbar({
              data: {
                userFriendlyMessage: 'Exporting starred results failed.',
                detailedInfoFromBE: err?.error?.message ? [err.error.message] : null,
              }, isAddressListSnackbar: true,
            }, err?.status);
          },
        })
        .add(() => {
          this.store.dispatch(OngoingActions.removeOngoingAction({ ongoingAction: OngoingActionsEnum.EXPORTING }));
        });
  }

  /**
   * Opens a popup to select in which languages should be exported.
   * Export place names in selected languages after the user clicks "Export" or closes the popup on "Close".
   */
  exportPlaceNames() {
    this.selectedLocations$.pipe(
        take(1), // limit to 1 emission, has to be placed here since below filter limits emission
        concatLatestFrom(() => this.accountID$),
        switchMap(([selectedLocations, accountID]) => {
          const filteredLocations = this.locationListApi.getFilteredLocations();
          return this.dialog.open(ExportPlaceNamesPopupComponent, {
            minWidth: '420px',
            data: {
              areLocationsSelected: !!selectedLocations.length,
              areLocationsFiltered: !!filteredLocations?.length,
            },
          }).afterClosed()
              .pipe(
                  map((res: {
                    locationSubset: SelectedLocationsRadioButton,
                    languageCodes: string[]
                  }) => ({ dialogRes: res, accountID, selectedLocations, filteredLocations})),
                  filter((res) => !!res.dialogRes),
              );
        }),
        // side effect
        tap(() => {
          this.store.dispatch(OngoingActions.addOngoingAction({ ongoingAction: OngoingActionsEnum.EXPORTING }));
          sendToGoogleAnalytics('Export location data', 'Export place names', 'Export place names as CSV');
        }),
        switchMap((res) => this.apiService.exportPlaceNames(
            res.accountID, res.dialogRes.languageCodes, this.createFilterMapFromLocationSubset(
                res.dialogRes.locationSubset, res.filteredLocations, res.selectedLocations))),
    ).subscribe({
      next: (res) => {
        this.feedbackService.showSuccessSnackbar({
          data: {
            userFriendlyMessage: 'Export to Google Sheets successful!',
            actionButtons: [{
              text: 'Open',
              action: () => window.open(res?.result, '_blank'),
            }],
          },
          // don't close automatically
          duration: null,
        });
        window.open(res?.result, '_blank');
      },
      error: (err: HttpErrorResponse) => {
        this.feedbackService.showAlertSnackbar({
          data: {
            userFriendlyMessage: 'Exporting place names failed.',
            detailedInfoFromBE: err?.error?.message ? [err.error.message] : null,
          },
        }, err?.status);
      },
    }).add(() => {
      this.store.dispatch(OngoingActions.removeOngoingAction({ ongoingAction: OngoingActionsEnum.EXPORTING }));
    });
  }

  /**
   * Sets lastUpdateErrorMessage to undefined and updates the BLS location.
   * @param location Location for which the update error should be ignored.
   */
  ignoreLastUpdateError(location: Location) {
    if (location.lastUpdateErrorMessage) {
      const clonedLocation = cloneDeep(location);
      clonedLocation.lastUpdateErrorMessage = undefined;
      this.accountID$
          .pipe(
              take(1),
              switchMap((accountID) => this.apiService
                  .updateDataInBLS(accountID, clonedLocation)),
              takeUntil(this.componentDestroyed),
          )
          .subscribe({
            next: (res) => this.store.dispatch(LocationsRefreshActions.ignoreUpdateError({location: res?.result})),
            error:
                (err: HttpErrorResponse) => {
                  this.feedbackService.showAlertSnackbar({
                    data: {
                      userFriendlyMessage: 'Resetting last update error failed.',
                      detailedInfoFromBE: err?.error?.message ? [err.error.message] : null,
                    },
                    isAddressListSnackbar: true,
                  }, err?.status);
                },
          });
    }
  }

  /**
   * Trigger a location-bound reviews sync call
   * @param location
   */
  syncReviews(location: Location) {
    this.accountID$.pipe(
        take(1), // limit to 1 emission, has to be placed here since below filter limits emission
        switchMap((accountID) => this.feedbackService.showConfirmationPopup({
              data: {
                title: `Sync reviews for location: ${location.storeCode}`,
                text: `Do you really wish to sync reviews for this location?`,
                submitButtonText: 'Yes',
                cancelButtonText: 'No',
                confirmationDialogType: ConfirmationDialogType.INFO,
              },
            })
                .pipe(
                    map((res: boolean) => res ? accountID : null),
                    filter((accountID) => !!accountID)),
        ),
        tap(() => {  // side effect
              this.store.dispatch(OngoingActions.addOngoingAction(
                  { ongoingAction: OngoingActionsEnum.SYNC_REVIEWS }));
          sendToGoogleAnalytics(
              'Single-location action',
              `Sync reviews`,
              `Sync reviews for location ID: ${location.id}`,
          );
        }),
        switchMap((accountID) => this.apiService.syncLocationReviews(accountID, location.storeCode)),
    )
        // no takeUntil() here since this action should not be interrupted even if component
        // gets destroyed since it might take a while to do the sync
        .subscribe({
          next: (res) => {
            if (res.status === ApiResponseStatus.ERROR) {
              this.feedbackService.showAlertSnackbar({
                data: {
                  userFriendlyMessage: res.message,
                },
                isAddressListSnackbar: true,
                duration: 5000,
              }, 0);
            } else {
              this.feedbackService.showSuccessSnackbar({
                data: {
                  userFriendlyMessage: res.message,
                },
                isAddressListSnackbar: true,
              });
            }
          },
          error: (err: HttpErrorResponse) => {
            this.feedbackService.showAlertSnackbar({
              data: {
                userFriendlyMessage: `There was an error with syncing reviews for location: ${location.storeCode}`,
                detailedInfoFromBE: err?.error?.message ? [err?.error?.message] : null,
              },
              isAddressListSnackbar: true,
              duration: 5000,
            }, err?.status);
          },
        }).add(() =>
        this.store.dispatch(OngoingActions.removeOngoingAction(
            { ongoingAction: OngoingActionsEnum.SYNC_REVIEWS })),
    );
  }

  /**
   * Context menu options 'shortcuts'
   * @param locationId
   * @param locationDetail
   */
  activateLocationDetail(locationId: number, locationDetail: LocationDetail) {
    this.store.dispatch(toggleLocationDetail({ id: locationId, detail: locationDetail }));
  }

  /**
   * Export GBP users to CSV/GOOGLE SHEETS
   * @param locationLevel single / group
   */
  exportGbpUsers(locationLevel: LocationLevel): void {
    const adminType = locationLevel === LocationLevel.GROUP ? LocationLevel.GROUP : 'location';
    // SHOW LOADING BAR
    this.store.dispatch(OngoingActions.addOngoingAction({ ongoingAction: OngoingActionsEnum.EXPORTING }));

    sendToGoogleAnalytics(
        'Export GMB users',
        `Export ${adminType} admins`,
        `Export ${adminType} admins as 'Sheet`);

    this.account$.pipe(
        take(1),
        switchMap((account) => this.apiService.getGbpUsers(account.id, locationLevel).pipe(
            map((res) => ({ gbpUsersApiRes: res, account })),
        )),
    ).subscribe({
      next: (res) => {
        // EXPORT TO GOOGLE SHEETS
        // PARTIAL SUCCESS
        if (res?.gbpUsersApiRes?.status === ApiResponseStatus.OTHER &&
            res?.gbpUsersApiRes?.result?.storeCodesNotFound) {
          this.feedbackService.showWarningSnackbar({
            data: {
              userFriendlyMessage: `Export to Google Sheets was successful but it failed for some locations – most likely because we don't OWN the locations below.`,
              detailedInfoFromBE: ['Affected locations: ' + res?.gbpUsersApiRes?.result?.storeCodesNotFound.join(', ')],
              actionButtons: [{
                text: 'Open Google Sheet',
                action: () => window.open(res?.gbpUsersApiRes?.result?.sheetUrl, '_blank'),
              }],
            },
            duration: null, // don't close snackbar
          });
          // SUCCESS
        } else {
          this.feedbackService.showSuccessSnackbar({
            data: {
              userFriendlyMessage: 'Export to Google Sheets successful!',
              actionButtons: [{
                text: 'Open',
                action: () => window.open(res?.gbpUsersApiRes?.result?.sheetUrl, '_blank'),
              }],
            },
            duration: null, // don't close snackbar
          });
        }
        window.open(res?.gbpUsersApiRes?.result?.sheetUrl, '_blank');
      }, error: (err: HttpErrorResponse) => {
        this.feedbackService.showAlertSnackbar({
          data: {
            userFriendlyMessage: `Exporting ${adminType} admins to Google Sheets failed.`,
            detailedInfoFromBE: err?.error?.message ? [err?.error?.message] : null,
          },
        }, err?.status);
      },
      // HIDE LOADING BAR
    }).add(() => this.store.dispatch(OngoingActions.removeOngoingAction({ ongoingAction: OngoingActionsEnum.EXPORTING })));
  }

  /**
   * TODO: add loading indication
   * @param coverPhotoURL
   */
  openOriginalCoverPhotoInNewTab(coverPhotoURL: string) {
    this.apiService.getOriginalPhoto(coverPhotoURL)
        .pipe(takeUntil(this.componentDestroyed))
        .subscribe({
          next: (originalURL) => {
            window.open(originalURL, '_blank');
          }, error: (err: HttpErrorResponse) => {
            this.feedbackService.showAlertSnackbar({
              data: {
                userFriendlyMessage: 'Retrieving URL of original cover photo failed.',
                detailedInfoFromBE: err?.error?.message ? [err.error.message] : null,
              }, isAddressListSnackbar: true,
            }, err?.status);
          },
        });
  }


  /*************************************************************************************************
   *                                        HELPERS                                                *
   ************************************************************************************************/


  /**
   * Formats the BE response presumed to contain errors by checking `result`,
   * iterating it if applicable, and defaulting to `message`.
   * @param resp
   * @return {string[]} errorMessages - a list of errors to be rendered in an HTML <ul>/<ol>
   */
  consolidateApiResponseErrors = (
      resp: ApiResponseWithPotentialErrors,
  ): string[] => {
    let errorMessages: string[] = [];

    if (resp.result) {
      const errorMap = convertJsonToMap(resp.result);
      errorMap.forEach((errorMessage, errorKey) => {
        // figure out if the current error key is a valid `fieldId`
        if (getFieldGroupOfFieldId(errorKey)) {
          const fieldName = getFullNameForFieldID(errorKey);
          errorMessages.push(`${fieldName}: ${errorMessage}`);
        } else {
          errorMessages.push(`Store code ${errorKey}: ${errorMessage}`);
        }
      });
    } else {
      errorMessages.push(resp.message);
    }

    return errorMessages;
  };

  /**
   * Show error column, filter for update errors and update the list
   * @private
   */
  private filterForGbpErrors(): void {
    // set filter
    this.locationListApi.setFilter(LSCField.lastUpdateErrorMessage, {
      filterType: 'text',
      type: 'notBlank'
    });
    // make sure column is visible
   this.locationListApi.toggleColumnVisibility(LSCField.lastUpdateErrorMessage, true);
    // TODO refactor after BLS-5472
    this.store.dispatch(LocationsRefreshActions.getEntireLocationList({}));
  }

  /**
   * Creates an Address Filter from a string that defines a subset of locations (all, filtered or
   * selected). Used only for placename-export.
   * The BE works this way since filtering was done there prior AG-GRID. By sending just store codes,
   * we effectively select locations that should appear in the export.
   * @param locationSubset Defines a subset of locations (all, filtered or selected locations)
   * @param filteredLocations
   * @param selectedLocations
   * @deprecated - Have BE rework place name export to accept list of IDs and then use
   */
  private createFilterMapFromLocationSubset(locationSubset: SelectedLocationsRadioButton,
                                            filteredLocations: Location[],
                                            selectedLocations: Location[]): FilterMap {
    switch (locationSubset) {
      case SelectedLocationsRadioButton.FILTERED:  // 'filtered' locations -> only storeCode filter since grid does the filtering
        return { storeCode: filteredLocations.map(l => l.storeCode) };
      case SelectedLocationsRadioButton.SELECTED:  // 'selected' locations -> only storeCode filter
        return { storeCode: selectedLocations.map(l => l.storeCode) };
      default:
        return {};  // 'all' locations -> no filter
    }
  }

  /**
   * Returns a list of location IDs from a subset of locations (all, filtered or
   * selected).
   * @param locationSubset Defines a subset of locations (all, filtered or selected locations)
   * @param filteredLocations
   * @param selectedLocations
   */
  private getLocationsIdsFromLocationSubset(locationSubset: SelectedLocationsRadioButton,
                                            filteredLocations: Location[],
                                            selectedLocations: Location[]): number[] {
    switch (locationSubset) {
      case SelectedLocationsRadioButton.FILTERED:  // 'filtered' locations -> only storeCode filter since grid does the filtering
        return filteredLocations.map(l => l.id);
      case SelectedLocationsRadioButton.SELECTED:  // 'selected' locations -> only storeCode filter
        return selectedLocations.map(l => l.id);
      default:
        return [];  // 'all' locations -> no filter
    }
  }

  /**
   * Returns the IDs of currently filtered locations as an Observable.
   */
  public getFilteredLocationIds$(): Observable<number[]> {
    return of(this.locationListApi.getFilteredLocations().map(l => l.id));
  }


}

